import { useEffect } from 'react'
import { useQueryClient } from 'react-query'
import { Formik } from 'formik'
import { Divider, Wrapper } from '@farewill/ui'

import { BRAND_ID } from 'config/brands'
import useApi from 'lib/effects/api'
import { Constants } from 'lib/models/constants'
import { QuoteResponseDataFuneralPlan } from 'lib/types'
import { isFuneralPlanLead } from 'lib/types/funeral-plans'
import { formatValuesForFormik } from 'utils/helpers'

import { useLeadContext } from '../context'
import QuoteForm from '../quote-form'

import Brochure from './brochure'
import { Celebration } from 'components/celebration-form-fields'
import ContactDetails from './contact-details'
import CouplePlan from './couple-plan'
import CoveredIndividual from './covered-individual'
import NominatedRepresentative from './nominated-representative'
import PaymentQuestions from './payment-questions'
import Plan from './plan'
import QualifyingQuestions from './qualifying-questions'
import RaisingMoneyForCharity from './raising-money-for-charity'
import VulnerabilityCheck from './vulnerability-check'
import Vulnerabilities from './vulnerabilities'
import { Crematoria } from './crematoria'

type Props = {
  disabled: boolean
}

const FuneralPlanForm = ({ disabled }: Props): React.ReactElement => {
  const { onSubmit, lead } = useLeadContext()
  const { brandId } = lead.attributes.contact
  const [{ data: quote }, makeRequest] = useApi<QuoteResponseDataFuneralPlan>()
  const cache = useQueryClient()

  useEffect(() => {
    const getHasEnoughDataForQuote = (): boolean => {
      if (!isFuneralPlanLead(lead.attributes)) {
        return false
      }
      const constants = cache.getQueryData('constants') as Constants
      const packageOffersCelebration =
        'packageSlug' in lead.attributes.quoteAttributes &&
        !!lead.attributes.quoteAttributes.packageSlug &&
        Object.keys(
          constants?.attributes?.celebrationPrices?.[
            lead.attributes.quoteAttributes.packageSlug
          ] ?? {}
        ).length > 0

      return (
        !!lead.id &&
        !!lead.attributes.quoteAttributes.paymentType &&
        (!packageOffersCelebration ||
          !!lead.attributes.quoteAttributes.celebration) &&
        (lead.attributes.quoteAttributes.celebration?.purchase !== 'yes' ||
          (!!lead.attributes.quoteAttributes.celebration?.type &&
            !!lead.attributes.quoteAttributes.urnType))
      )
    }

    const hasEnoughDataForQuote = getHasEnoughDataForQuote()

    if (hasEnoughDataForQuote) {
      makeRequest({
        url: '/api/quotes',
        method: 'POST',
        data: {
          data: {
            type: 'quotes',
            attributes: {
              product: 'funeral_plan',
              leadId: lead.id,
            },
          },
        },
      })
    }
  }, [lead, lead.attributes, cache, makeRequest])

  if (!isFuneralPlanLead(lead.attributes)) {
    return <></>
  }

  return (
    <Formik
      /** Passing the lead id as a key forces React to remount the component. This was needed
       * because when clicking 'View linked lead' from the first to the second lead, the form
       * was not resetting with the data of the 2nd lead.  */
      key={lead.id}
      initialValues={formatValuesForFormik(lead.attributes)}
      onSubmit={onSubmit}
    >
      <QuoteForm disabled={disabled}>
        <ContactDetails />
        <Divider margin={['L', 0]} />
        <Brochure />
        <Divider margin={['L', 0]} />
        <Vulnerabilities />
        <Divider margin={['L', 0]} />
        <CouplePlan />
        <Divider margin={['L', 0]} />
        <Plan />
        <Divider margin={['L', 0]} />
        <CoveredIndividual dataTestid="covered-individual" />
        <QualifyingQuestions />
        <NominatedRepresentative />
        {brandId === BRAND_ID.farewill && <RaisingMoneyForCharity />}
        <Divider margin={['L', 0]} />
        <Wrapper margin={['L', 0]}>
          <VulnerabilityCheck />
        </Wrapper>
        {brandId === BRAND_ID.simplicity && (
          <>
            <Divider margin={['L', 0]} />
            <Crematoria />
          </>
        )}
        {/* divider in Celebration as it can render a blank if no options */}
        <Celebration />
        <Divider margin={['L', 0]} />
        <PaymentQuestions quote={quote?.attributes} />
      </QuoteForm>
    </Formik>
  )
}

export default FuneralPlanForm
